import { getContentByUrl } from 'widgets/toolbox/ajax';
import { timeout } from 'widgets/toolbox/util';

/**
 * @description CountrySelectorDialog implementation
 * @param Modal Base widget for extending
 * @returns CountrySelectorDialog class
 */
export default function (Modal: ReturnType<typeof import('widgets/global/Modal').default>) {
    /**
     * @category widgets
     * @subcategory header
     * @class CountrySelectorDialog
     * @augments Modal
     * @classdesc CountrySelectorDialog widget, that contains all needed logic to display/update country selector component.
     */
    class CountrySelectorDialog extends Modal {
        longWaitingTimeout: ReturnType<typeof timeout>|undefined;

        isCountrySelectOpened = false;

        /**
         * @description Returns Widget configuration object
         */
        prefs() {
            return {
                actionUrl: '',
                classesActivated: 'm-activated',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization logic
         * @listens "countrySelector.show"
         */
        init(): void {
            super.init();
            this.eventBus().on('show.country.selector', 'showCountrySelector');
        }

        /**
         * @description A method, called when event `click` happens on country select icon
         * - Shows countrySelector panel
         * - Update content of the panel
         */
        showCountrySelector(): void {
            this.showModalPreview(null);
            this.isCountrySelectOpened = true;
            this.getContent();
        }

        /**
         * @description Close country select Dialog
         * @listens dom#click
         */
        cancel(): void {
            this.isCountrySelectOpened = false;
            super.cancel();
        }

        /**
         * @description Update country select panel content
         * Fetch actual content in case when it needed. Use flag `needUpdate` for this
         */
        getContent() : Promise<Record<string, unknown>|null> {
            if (!this.isCountrySelectOpened) {
                return Promise.resolve(null);
            }

            return getContentByUrl(this.prefs().actionUrl)
                .then((response) => {
                    return this.render(undefined, {}, this.ref(this.prefs().refDialog), String(response)).then(() => {
                        this.showModal({
                            attributes: {
                                'data-tau-unique': 'country_selector_dialog'
                            }
                        });
                    }).then(() => response);
                })
                .catch(() => {
                    this.eventBus().emit('alert.show', { errorCode: 500 });
                    this.close();

                    return Promise.resolve(null);
                }).finally(() => {
                    this.toggleSpinner(false);
                });
        }

        /**
         * @description Fires `alert.show` event to be handled with Global Alerting System
         * @param accessibilityAlert - Alert string
         */
        accessibilityAlert(accessibilityAlert: string): void {
            this.eventBus().emit('alert.show', {
                accessibilityAlert
            });
        }
    }

    return CountrySelectorDialog;
}
